<script setup lang="ts">
import { X } from 'lucide-vue-next'
import { VerifiedMarker } from '@/components/ui/verified-marker'
import { MemberAvatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

export interface InviteRowItem {
  member_id: number | null
  first_name?: string
  last_name?: string
  email: string | null
  phone?: string | null
  avatar_url: string | null
  display_name: string | null
  role: string
  [key: string]: any
}

export interface RoleOption {
  value: string
  label: string
}

defineProps<{
  rows: InviteRowItem[]
  roles: RoleOption[]
}>()

const emit = defineEmits<{
  remove: [index: number]
  'update:role': [index: number, role: string]
}>()

const displayName = (row: InviteRowItem) => {
  return row.display_name
    || `${row.first_name || ''} ${row.last_name || ''}`.trim()
    || row.email
    || row.phone
    || 'Unknown'
}
</script>

<template>
  <div v-if="rows.length > 0" class="divide-y divide-gray-200">
    <div
      v-for="(row, index) in rows"
      :key="row.email || row.phone || index"
      class="flex items-center gap-3 py-2"
    >
      <MemberAvatar :avatar_url="row.avatar_url" :verified="row.verified" />
      <div class="flex flex-col min-w-0 flex-1">
        <span class="truncate text-sm font-medium flex items-center gap-1">
          {{ displayName(row) }}
          <VerifiedMarker v-if="row.verified" class="shrink-0" />
        </span>
        <span v-if="row.email || row.phone" class="truncate text-xs text-muted-foreground">
          {{ row.email || row.phone }}
        </span>
      </div>
      <Tabs :model-value="row.role" @update:model-value="v => emit('update:role', index, String(v))">
        <TabsList class="h-6 p-0.5">
          <TabsTrigger
            v-for="r in roles"
            :key="r.value"
            :value="r.value"
            class="h-5 px-1.5 text-xs"
          >{{ r.label }}</TabsTrigger>
        </TabsList>
      </Tabs>
      <Button variant="ghost" size="sm" class="h-6 w-6 p-0 shrink-0" @click="emit('remove', index)">
        <X class="h-3 w-3" />
      </Button>
    </div>
  </div>
</template>
